import type { useGetCouponApplication } from "@circle-react/components/Paywalls/Checkout/hooks/useGetCouponApplication";
import { isInstallmentsPrice } from "@circle-react/helpers/paywallPriceHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { PaywallPriceScenario } from "@circle-react/types/PaywallPrice";

import { Price } from "./Price";
import { Subtitle } from "./Price/Subtitle";
import { getCouponInstallmentQualifier } from "./Price/Value";
import type { PriceComponentProps, PriceScenariosProps } from "./types";

const scenariosResolver: Record<
  PaywallPriceScenario,
  Array<(props: PriceScenariosProps) => React.ReactNode>
> = {
  value: [Price],
  trial_then_value: [Price.Badge, Price.ValueWithArrow],
  trial_then_upfront_then_value: [
    Price.Badge,
    Price.IntroFeeWithArrow,
    Price.ValueWithArrow,
  ],
  upfront_then_value: [Price.IntroFee, Price.ValueWithArrow],
  upfront_then_trial_then_value: [
    Price.IntroFee,
    Price.BadgeWithArrow,
    Price.ValueWithArrow,
  ],
};

interface PriceDisplayProps extends Omit<
  PriceComponentProps,
  "couponApplications"
> {
  className?: string;
  couponApplications?: Pick<
    ReturnType<typeof useGetCouponApplication>,
    "applications"
  >;
}

export const PriceDisplay = ({
  price,
  paywall,
  reducedPrice,
  couponApplications,
  className,
}: PriceDisplayProps) => {
  const components = scenariosResolver[price.scenario];

  // `applications` is a flat list ordered by payment position. The Value/
  // ValueWithArrow component is always last, so installment applications start
  // at valueIndex. Count how many of those actually receive a discount.
  const valueIndex = components.length - 1;
  const installmentsCovered = couponApplications?.applications
    .slice(valueIndex)
    .filter(application => application.discount_amount > 0).length;

  // The qualifier renders once, as a block below the full price row. We guard
  // with installmentsCovered > 0 so the qualifier only appears when the coupon
  // actually applies to this specific price.
  const qualifier =
    isInstallmentsPrice(price) && (installmentsCovered ?? 0) > 0
      ? getCouponInstallmentQualifier(
          installmentsCovered,
          price.installments_count ?? 1,
        )
      : undefined;

  return (
    <div key={price.id} className={classNames("leading-6", className)}>
      {components.map((component, index) =>
        component({
          price,
          paywall,
          reducedPrice,
          couponApplication: couponApplications?.applications[index],
        }),
      )}
      {qualifier && <Subtitle subtitle={qualifier} />}
    </div>
  );
};
